/**
 * ManagementLayout Component
 *
 * Dedicated layout for Platform Management Console
 * Accessible by: superadmin, enterprise_admin, channel_partner_admin, partner_admin
 * Each role has scoped access (superadmin sees all, others see their scope)
 *
 * Theme: Purple (#9333ea)
 * Features: Organizations, Users, Settings, Platform Analytics
 */

import { useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ManagementSidebar from './ManagementSidebar';
import ManagementHeader from './ManagementHeader';

// Roles that can access Platform Management Console
const PLATFORM_ROLES = ['superadmin', 'enterprise_admin', 'channel_partner_admin', 'partner_admin'];

const ManagementLayout: React.FC = () => {
  const { user, canAccessPlatform } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Must be logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has platform access
  if (!canAccessPlatform()) {
    // Read-only enterprise users go to Enterprise Portal
    if (user.role === 'enterprise_user') {
      return <Navigate to="/enterprise" replace />;
    }
    // Read-only partner users go to Partner Portal
    if (['channel_partner_user', 'partner_user'].includes(user.role)) {
      return <Navigate to="/partner" replace />;
    }
    // Clinical roles go to Dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 w-full overflow-x-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Management Sidebar */}
      <ManagementSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content Area */}
      <div className="md:pl-64 w-full overflow-x-hidden">
        {/* Management Header */}
        <ManagementHeader onMenuToggle={() => setSidebarOpen(true)} />

        {/* Page Content */}
        <main className="py-8 px-4 sm:px-6 lg:px-8 w-full overflow-x-hidden">
          <div className="max-w-7xl mx-auto w-full overflow-x-hidden">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default ManagementLayout;
